import { useQuery } from "@apollo/client/react";
import { AlertTriangle, Building2, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";
import {
  GetOrganizationsForUserSelectDocument,
  type GetOrganizationsForUserSelectQuery,
} from "~/features/admin/users/gqls";
import {
  type BulkUserOrganizationFailure,
  type BulkUserOrganizationRunResult,
  type BulkUserOrganizationTarget,
  useBulkUserOrganizationUpdate,
} from "~/features/admin/users/hooks";
import type { UserRow } from "~/features/admin/users/types";

interface BulkUserOrganizationChangeDialogProps {
  open: boolean;
  onClose: () => void;
  users: UserRow[];
  onCompleted: () => void;
}

export function BulkUserOrganizationChangeDialog({
  open,
  onClose,
  users,
  onCompleted,
}: BulkUserOrganizationChangeDialogProps) {
  const { t } = useTranslation();
  const { data, loading: destinationsLoading } = useQuery<GetOrganizationsForUserSelectQuery>(
    GetOrganizationsForUserSelectDocument,
    {
      skip: !open,
    },
  );
  const { run, running, progress } = useBulkUserOrganizationUpdate();
  const [destinationShortCode, setDestinationShortCode] = useState("");
  const [initialResult, setInitialResult] = useState<BulkUserOrganizationRunResult | null>(null);
  const [finalResult, setFinalResult] = useState<BulkUserOrganizationRunResult | null>(null);
  const [hasRun, setHasRun] = useState(false);

  const targets = useMemo<BulkUserOrganizationTarget[]>(
    () =>
      users.map((user) => ({
        userId: user.id,
        displayName: user.displayName,
        email: user.email,
        currentOrganizationName: user.organization.name,
      })),
    [users],
  );
  const destinations = useMemo(
    () =>
      (data?.organizations ?? []).filter(
        (organization) => !users.every((user) => user.organization.shortCode === organization.shortCode),
      ),
    [data?.organizations, users],
  );
  const destination = destinations.find((organization) => organization.shortCode === destinationShortCode);
  const needsConfirmation = initialResult !== null && initialResult.confirmations.length > 0 && finalResult === null;
  const completedResult = finalResult ?? (initialResult?.confirmations.length === 0 ? initialResult : null);

  const reset = () => {
    setDestinationShortCode("");
    setInitialResult(null);
    setFinalResult(null);
    setHasRun(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (next || running) return;
    const shouldRefresh = hasRun;
    reset();
    onClose();
    if (shouldRefresh) onCompleted();
  };

  const handleInitialRun = async () => {
    if (!destinationShortCode) return;
    setHasRun(true);
    const result = await run(targets, destinationShortCode, false);
    setInitialResult(result);
  };

  const handleConfirmedRun = async () => {
    if (!destinationShortCode || !initialResult) return;
    const confirmationTargets = initialResult.confirmations.map(({ message: _message, ...target }) => target);
    const confirmed = await run(confirmationTargets, destinationShortCode, true);
    setFinalResult({
      succeeded: [...initialResult.succeeded, ...confirmed.succeeded],
      confirmations: [],
      failures: [...initialResult.failures, ...confirmed.failures, ...confirmed.confirmations],
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("feature.users.bulkOrganizationChange.title")}</DialogTitle>
          <DialogDescription>
            {t("feature.users.bulkOrganizationChange.description", { count: users.length })}
          </DialogDescription>
        </DialogHeader>

        {completedResult ? (
          <CompletedStep result={completedResult} onClose={() => handleOpenChange(false)} />
        ) : needsConfirmation && initialResult ? (
          <ConfirmationStep
            destinationName={destination?.name ?? destinationShortCode}
            succeededCount={initialResult.succeeded.length}
            confirmations={initialResult.confirmations}
            failures={initialResult.failures}
            running={running}
            progress={progress}
            onConfirm={() => void handleConfirmedRun()}
            onClose={() => handleOpenChange(false)}
          />
        ) : (
          <SelectionStep
            targets={targets}
            destinations={destinations}
            destinationsLoading={destinationsLoading}
            destinationShortCode={destinationShortCode}
            onDestinationChange={setDestinationShortCode}
            running={running}
            progress={progress}
            onRun={() => void handleInitialRun()}
            onClose={() => handleOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ProgressValue {
  done: number;
  total: number;
}

function SelectionStep({
  targets,
  destinations,
  destinationsLoading,
  destinationShortCode,
  onDestinationChange,
  running,
  progress,
  onRun,
  onClose,
}: {
  targets: BulkUserOrganizationTarget[];
  destinations: GetOrganizationsForUserSelectQuery["organizations"];
  destinationsLoading: boolean;
  destinationShortCode: string;
  onDestinationChange: (value: string) => void;
  running: boolean;
  progress: ProgressValue | null;
  onRun: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor="bulk-user-organization-destination" className="text-12-medium text-fg-2">
          {t("feature.users.organizationChange.destinationOrganization")}
        </label>
        <Select
          value={destinationShortCode}
          onValueChange={onDestinationChange}
          disabled={running || destinationsLoading || destinations.length === 0}
        >
          <SelectTrigger id="bulk-user-organization-destination">
            <SelectValue placeholder={t("feature.users.organizationChange.selectDestination")} />
          </SelectTrigger>
          <SelectContent>
            {destinations.map((organization) => (
              <SelectItem key={organization.id} value={organization.shortCode}>
                {organization.name} ({organization.shortCode})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!destinationsLoading && destinations.length === 0 && (
          <p className="text-12-regular text-fg-3">{t("feature.users.organizationChange.noDestination")}</p>
        )}
      </div>

      <TargetList targets={targets} />
      {running && progress && <Progress progress={progress} />}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={running}>
          {t("ui.cancel")}
        </Button>
        <Button type="button" onClick={onRun} disabled={!destinationShortCode || running || targets.length === 0}>
          {running ? <Loader2 className="animate-spin" /> : <Building2 />}
          {running
            ? t("feature.users.bulkOrganizationChange.processing")
            : t("feature.users.bulkOrganizationChange.run", { count: targets.length })}
        </Button>
      </DialogFooter>
    </div>
  );
}

function ConfirmationStep({
  destinationName,
  succeededCount,
  confirmations,
  failures,
  running,
  progress,
  onConfirm,
  onClose,
}: {
  destinationName: string;
  succeededCount: number;
  confirmations: BulkUserOrganizationFailure[];
  failures: BulkUserOrganizationFailure[];
  running: boolean;
  progress: ProgressValue | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-[var(--radius-md)] border border-warning bg-warning-bg p-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <div className="space-y-1">
          <h3 className="text-14-bold text-fg-1">{t("feature.users.bulkOrganizationChange.confirmationTitle")}</h3>
          <p className="text-14-regular text-fg-2">
            {t("feature.users.bulkOrganizationChange.confirmationDescription", {
              count: confirmations.length,
              organization: destinationName,
            })}
          </p>
        </div>
      </div>

      <div className="text-14-regular text-fg-2">
        {t("feature.users.bulkOrganizationChange.firstPassSummary", {
          succeeded: succeededCount,
          confirmation: confirmations.length,
          failed: failures.length,
        })}
      </div>
      <FailureList failures={confirmations} tone="warning" />
      {failures.length > 0 && <FailureList failures={failures} tone="danger" />}
      {running && progress && <Progress progress={progress} />}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={running}>
          {t("feature.users.bulkOrganizationChange.finishWithoutConfirmation")}
        </Button>
        <Button type="button" onClick={onConfirm} disabled={running}>
          {running && <Loader2 className="animate-spin" />}
          {t("feature.users.bulkOrganizationChange.confirm", { count: confirmations.length })}
        </Button>
      </DialogFooter>
    </div>
  );
}

function CompletedStep({ result, onClose }: { result: BulkUserOrganizationRunResult; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="space-y-1 text-14-regular text-fg-1">
        <div>{t("feature.users.bulkOrganizationChange.resultSucceeded", { count: result.succeeded.length })}</div>
        {result.failures.length > 0 && (
          <div className="text-danger">
            {t("feature.users.bulkOrganizationChange.resultFailed", { count: result.failures.length })}
          </div>
        )}
      </div>
      {result.succeeded.length > 0 && <TargetList targets={result.succeeded} />}
      {result.failures.length > 0 && <FailureList failures={result.failures} tone="danger" />}
      <DialogFooter>
        <Button type="button" onClick={onClose}>
          {t("ui.close")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function TargetList({ targets }: { targets: BulkUserOrganizationTarget[] }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <p className="text-12-medium text-fg-2">
        {t("feature.users.bulkOrganizationChange.targets", { count: targets.length })}
      </p>
      <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border-default p-3 text-14-regular">
        {targets.map((target) => (
          <li key={target.userId} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="font-medium text-fg-1">{target.displayName || target.email}</span>
            <span className="text-12-regular text-fg-3">
              {target.email} · {target.currentOrganizationName}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FailureList({ failures, tone }: { failures: BulkUserOrganizationFailure[]; tone: "warning" | "danger" }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <p className="text-12-medium text-fg-2">
        {tone === "warning"
          ? t("feature.users.bulkOrganizationChange.membershipRemovals")
          : t("feature.users.bulkOrganizationChange.failures")}
      </p>
      <ul className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-border-default p-3 text-14-regular">
        {failures.map((failure) => (
          <li key={failure.userId} className="space-y-0.5">
            <div className="font-medium text-fg-1">{failure.displayName || failure.email}</div>
            <div className={tone === "danger" ? "text-danger" : "text-warning"}>{failure.message}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Progress({ progress }: { progress: ProgressValue }) {
  const { t } = useTranslation();
  const percentage = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
  return (
    <div className="space-y-1.5">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
        <div className="h-full rounded-full bg-primary-500" style={{ width: `${percentage}%` }} />
      </div>
      <div className="text-12-regular tabular-nums text-fg-3">
        {t("feature.users.bulkOrganizationChange.progress", { done: progress.done, total: progress.total })}
      </div>
    </div>
  );
}
